import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

const ITEM_HEIGHT = 80; // Approximate height of each notification item
const INITIAL_VISIBLE_ITEMS = 25; // Show same number as limited view
// Show exactly 25 items to match the initial limited view
const CONTAINER_HEIGHT = INITIAL_VISIBLE_ITEMS * ITEM_HEIGHT; // 2000px for 25 items
const OVERSCAN = 3; // Number of items to render outside visible area for smooth scrolling

const VirtualizedList = ({ items, renderItem, emptyMessage, showCount = true }) => {
  const [scrollTop, setScrollTop] = useState(0);
  const scrollContainerRef = useRef(null);

  // Calculate visible range
  const visibleRange = useMemo(() => {
    const start = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
    const visibleCount = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT);
    const end = Math.min(items.length, start + visibleCount + OVERSCAN * 2);
    return { start, end };
  }, [scrollTop, items.length]);

  // Get visible items
  const visibleItems = useMemo(() => {
    return items.slice(visibleRange.start, visibleRange.end).map((item, index) => ({
      ...item,
      virtualIndex: visibleRange.start + index,
    }));
  }, [items, visibleRange.start, visibleRange.end]);

  // Calculate spacer heights
  const topSpacerHeight = visibleRange.start * ITEM_HEIGHT;
  const bottomSpacerHeight = (items.length - visibleRange.end) * ITEM_HEIGHT;

  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  // Reset scroll when items change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
      setScrollTop(0);
    }
  }, [items.length]);

  if (items.length === 0) {
    return <Box sx={{ p: 2, textAlign: 'center' }}>{emptyMessage}</Box>;
  }

  return (
    <Box>
      {showCount && (
        <Box
          sx={{
            p: 1,
            px: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.75rem',
            }}
          >
            Showing all {items.length} notification{items.length !== 1 ? 's' : ''} (scroll to view)
          </Typography>
        </Box>
      )}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          height: CONTAINER_HEIGHT,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {/* Top spacer to maintain scroll position */}
        {topSpacerHeight > 0 && <Box sx={{ height: topSpacerHeight }} />}
        
        {/* Render visible items */}
        {visibleItems.map((item) => (
          <Box key={item._id || item.id}>
            {renderItem(item, item.virtualIndex)}
          </Box>
        ))}
        
        {/* Bottom spacer to maintain scroll position */}
        {bottomSpacerHeight > 0 && <Box sx={{ height: bottomSpacerHeight }} />}
      </Box>
    </Box>
  );
};

export default VirtualizedList;
